import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'
import { EXPENSE_METHODOLOGY } from '@/lib/constants/work-order-codes'

// Individual approver schema - sequence auto-managed by form based on array position
const timesheetApproverSchema = z.object({
  userProfileId: z.number().min(1, 'Field is required'),
  sequence: z.number().min(1, 'Field is required'),
})

// Individual expense approver schema - same structure as timesheet approver
const expenseApproverSchema = z.object({
  userProfileId: z.number().min(1, 'Field is required'),
  sequence: z.number().min(1, 'Field is required'),
})

// Define the timesheet and expense form schema - this is the single source of truth
export const timesheetAndExpenseFormSchema = z.object({
  selectedTimesheetCycle: z.string().min(1, 'Field is required'),
  approvers: z.array(timesheetApproverSchema).min(1, 'At least one approver is required'),
  selectedApprovalSequence: z.string().optional(),
  expenseMethodologyCode: z.string().min(1, 'Field is required'),
  expenseApprovers: z.array(expenseApproverSchema).optional().default([])
}).superRefine((data, ctx) => {
  // Timesheet approval sequence required when multiple approvers
  if (data.approvers.length > 1 && !data.selectedApprovalSequence) {
    zodValidationHelpers.requiredString(ctx, ['selectedApprovalSequence'])
  }
  
  // Expense approver required when methodology is OnlineApproval (expenses enabled)
  if (data.expenseMethodologyCode === EXPENSE_METHODOLOGY.ONLINE_APPROVAL) {
    if (!data.expenseApprovers || data.expenseApprovers.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expense approver is required when expenses are enabled',
        path: ['expenseApprovers']
      })
    }
  }
})

// Automatically infer the TypeScript type from the schema
export type TimesheetAndExpenseFormData = Expand<z.infer<typeof timesheetAndExpenseFormSchema>>
export type TimesheetApproverData = Expand<z.infer<typeof timesheetApproverSchema>>
export type ExpenseApproverData = Expand<z.infer<typeof expenseApproverSchema>>

